import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { Link } from 'react-router';
import {
  CONTRACT_STATUS_LABELS,
  INSTANCE_STATUS_LABELS,
  type ContractStatus,
  type InstanceStatus,
} from '@zentera/shared';
import { Badge, IconButton, StatusDot, Tooltip, type Tone } from '@zentera/ui';
import { fmt } from '../lib/format';

export const instanceTone: Record<InstanceStatus, Tone> = {
  online: 'success',
  degraded: 'warning',
  offline: 'danger',
  pending: 'muted',
  provisioning: 'info',
  maintenance: 'neutral',
};

export function InstanceStatusLabel({ status }: { status: InstanceStatus | null | undefined }) {
  if (!status) return <StatusDot tone="muted">Sem instância</StatusDot>;
  return (
    <StatusDot tone={instanceTone[status]} pulse={status === 'provisioning'}>
      {INSTANCE_STATUS_LABELS[status]}
    </StatusDot>
  );
}

const contractTone: Record<ContractStatus, Tone> = {
  active: 'neutral',
  onboarding: 'info',
  suspended: 'warning',
  terminated: 'muted',
};

export function ContractBadge({ status }: { status: ContractStatus }) {
  return <Badge tone={contractTone[status]}>{CONTRACT_STATUS_LABELS[status]}</Badge>;
}

/** Identifica registros criados pelo seed de demonstração. */
export function DemoTag() {
  return (
    <Tooltip content="Registro de demonstração (seed). Não representa um cliente real.">
      <span className="inline-flex h-4 items-center rounded-sm border border-dashed border-line-strong px-1 text-2xs font-medium text-fg-3">
        Demonstração
      </span>
    </Tooltip>
  );
}

export function BoolState({ value, yes, no, unknown = 'Sem informação' }: { value: boolean | null | undefined; yes: string; no: string; unknown?: string }) {
  if (value === null || value === undefined) return <StatusDot tone="muted">{unknown}</StatusDot>;
  return <StatusDot tone={value ? 'success' : 'danger'}>{value ? yes : no}</StatusDot>;
}

export function RelativeTime({ value, fallback = '—' }: { value: string | null | undefined; fallback?: string }) {
  if (!value) return <span className="text-fg-4">{fallback}</span>;
  return (
    <Tooltip content={fmt.dateTime(value)}>
      <time dateTime={value} className="text-fg-2">
        {fmt.relative(value)}
      </time>
    </Tooltip>
  );
}

export function CopyButton({ value, label = 'Copiar' }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <IconButton
      size="sm"
      label={done ? 'Copiado' : label}
      icon={done ? <Check className="text-success" /> : <Copy />}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    />
  );
}

export function TextLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={`text-fg hover:text-brand hover:underline underline-offset-2 ${className ?? ''}`} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

export function Placeholder({ children = '—' }: { children?: ReactNode }) {
  return <span className="text-fg-4">{children}</span>;
}
